/**
 * Opinion dataset API calls (specs/opinion-datasets.md, #1179). Errors carry the server's message.
 */
import API_ENDPOINTS from '../../../config/api';

async function request(url, options = {}) {
  const res = await fetch(url, { credentials: 'include', ...options });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return body;
}

const json = (method, payload) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload || {}),
});

export const listOpinionDatasets = () => request(API_ENDPOINTS.ADMIN_OPINION_DATASETS()).then((b) => b.datasets || []);
/** { dataset, statements } */
export const getOpinionDataset = (id) => request(API_ENDPOINTS.ADMIN_OPINION_DATASET(id));
export const updateOpinionDataset = (id, patch) => request(API_ENDPOINTS.ADMIN_OPINION_DATASET(id), json('PATCH', patch));
export const updateOpinionGroup = (id, key, patch) => request(API_ENDPOINTS.ADMIN_OPINION_DATASET_GROUP(id, key), json('PATCH', patch));
export const importOpinionDataset = (payload) => request(API_ENDPOINTS.ADMIN_OPINION_DATASET_IMPORT(), json('POST', payload)).then((b) => b.result);
export const searchSourceDocuments = (q) => request(API_ENDPOINTS.ADMIN_OPINION_SOURCE_DOCUMENTS(q)).then((b) => b.documents || []);

export const getInstanceOpinionDatasets = (instanceId) => request(API_ENDPOINTS.ADMIN_INSTANCE_OPINION_DATASETS(instanceId));
export const attachOpinionDataset = (instanceId, datasetId) =>
  request(API_ENDPOINTS.ADMIN_INSTANCE_OPINION_DATASET(instanceId, datasetId), { method: 'PUT' });
export const detachOpinionDataset = (instanceId, datasetId) =>
  request(API_ENDPOINTS.ADMIN_INSTANCE_OPINION_DATASET(instanceId, datasetId), { method: 'DELETE' });

/**
 * Draft every group's profile: the admin-only `opinion.build_profiles` job. It belongs
 * to no instance, so it is polled here rather than through useAiJob (which follows an
 * instance's socket room). Resolves with the job's result; onStage gets its stage label.
 */
export async function buildProfiles(datasetId, { onStage = () => {}, intervalMs = 2000 } = {}) {
  const { jobId } = await request(API_ENDPOINTS.AI_JOBS(), json('POST', { type: 'opinion.build_profiles', input: { datasetId } }));
  for (;;) {
    await new Promise((r) => setTimeout(r, intervalMs));
    const job = await request(API_ENDPOINTS.AI_JOB(jobId));
    if (job.stage) onStage(job.stage);
    if (job.status === 'done') return job.result;
    if (job.status === 'failed' || job.status === 'cancelled') throw new Error(job.error || `Job ${job.status}`);
  }
}
